/**
 * GSC Opportunities + Content decay cards.
 *
 * Shown on the project Tracking tab (single measurement surface).
 * Connect CTA jumps to #integrations for OAuth.
 *
 * Opportunities: high-impression queries stuck past position 10.
 * Decay: pages with >20% WoW impression drop → one-click refresh brief.
 */
import { Link, useNavigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import {
  ArrowDownRight,
  ExternalLink,
  FileEdit,
  Plug,
  Search,
  TrendingDown,
} from 'lucide-react';
import { toast } from 'sonner';
import type { GscDecayItem, GscOpportunity } from '@seo/types';

import { Surface } from '@/components/ui/surface';
import {
  Section,
  SectionDescription,
  SectionHeader,
  SectionTitle,
} from '@/components/ui/section';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/spinner';
import { EmptyState } from '@/components/ui/empty';
import { Callout } from '@/components/ui/callout';
import { fadeUp } from '@/components/motion';
import { summarizeApiError } from '@/lib/api';
import { useCreateBrief } from '@/features/briefs/use-create-brief';
import {
  useCreateGscRefreshBrief,
  useGscInsights,
} from '@/features/integrations/use-gsc-insights';

export function GscInsightsCards({ projectId }: { projectId: string }): JSX.Element {
  const insights = useGscInsights(projectId);

  if (insights.isLoading) {
    return (
      <Section>
        <SectionHeader>
          <div>
            <SectionTitle>Search Console</SectionTitle>
            <SectionDescription>Loading opportunities and content decay…</SectionDescription>
          </div>
        </SectionHeader>
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Spinner className="h-4 w-4" />
          Pulling Search Console data…
        </div>
      </Section>
    );
  }

  if (insights.error) {
    return (
      <Section>
        <Callout tone="danger" title="Could not load Search Console insights">
          {
            summarizeApiError(insights.error, {
              fallbackTitle: 'Search Console request failed',
            }).description
          }
        </Callout>
      </Section>
    );
  }

  const data = insights.data;
  if (!data) return <></>;

  const needsConnect =
    !data.configured ||
    !data.connected ||
    data.warnings.some((w) =>
      ['GSC_NOT_CONNECTED', 'GSC_SITE_MISSING', 'GSC_SCOPE_MISSING', 'GOOGLE_OAUTH_NOT_CONFIGURED'].includes(
        w.code,
      ),
    );

  if (needsConnect && data.opportunities.length === 0 && data.decay.length === 0) {
    return (
      <Section>
        <SectionHeader>
          <div>
            <SectionTitle>Search Console</SectionTitle>
            <SectionDescription>
              Connect Google Search Console to surface ranking opportunities and decaying pages.
            </SectionDescription>
          </div>
        </SectionHeader>
        <Surface
          tone="outline"
          className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
              <Plug className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium">
                {data.warnings[0]?.message ??
                  'Google Search Console is not connected for this project.'}
              </p>
              <p className="text-xs text-muted-foreground">
                Needs scope{' '}
                <code className="rounded bg-muted px-1 py-0.5 text-[11px]">
                  webmasters.readonly
                </code>
              </p>
            </div>
          </div>
          <Button asChild variant="outline" size="sm">
            {/* SPA navigation — a raw anchor here forced a full document
                reload and dropped the router/query/auth state. */}
            <Link
              to="/projects/$projectId"
              params={{ projectId }}
              hash="integrations"
            >
              Connect Google
            </Link>
          </Button>
        </Surface>
      </Section>
    );
  }

  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      animate="show"
      className="space-y-4"
    >
      {data.warnings.length > 0 ? (
        <Callout tone="warning" title="Search Console note">
          {data.warnings.map((w) => w.message).join(' · ')}
        </Callout>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <OpportunitiesCard
          projectId={projectId}
          items={data.opportunities}
          period={data.period}
          cached={data.cached}
        />
        <DecayCard projectId={projectId} items={data.decay} period={data.period} />
      </div>
    </motion.div>
  );
}

function OpportunitiesCard({
  projectId,
  items,
  period,
  cached,
}: {
  projectId: string;
  items: GscOpportunity[];
  period: { current_start: string; current_end: string } | null;
  cached: boolean;
}): JSX.Element {
  return (
    <Section>
      <SectionHeader>
        <div>
          <SectionTitle className="flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            GSC Opportunities
          </SectionTitle>
          <SectionDescription>
            High impressions, avg position worse than #10
            {period ? ` · ${period.current_start} → ${period.current_end}` : ''}
            {cached ? ' · cached' : ''}
          </SectionDescription>
        </div>
      </SectionHeader>

      {items.length === 0 ? (
        <EmptyState
          variant="inline"
          icon={Search}
          title="No ranking opportunities yet"
          description="Once Search Console shows queries with 100+ impressions past position 10, they’ll show up here."
        />
      ) : (
        <ul className="divide-y divide-border/60">
          {items.slice(0, 8).map((row) => (
            <OpportunityRow key={row.query} projectId={projectId} item={row} />
          ))}
        </ul>
      )}
    </Section>
  );
}

function OpportunityRow({
  projectId,
  item,
}: {
  projectId: string;
  item: GscOpportunity;
}): JSX.Element {
  const create = useCreateBrief();
  const navigate = useNavigate();

  const onCreate = async (): Promise<void> => {
    try {
      const brief = await create.mutateAsync({
        project_id: projectId,
        target_keyword: item.query,
        delivery: 'markdown',
      });
      toast.success('Brief started', {
        description: `Researching “${item.query}” — usually 2–3 minutes.`,
      });
      navigate({ to: '/briefs/$briefId', params: { briefId: brief.id } });
    } catch (e) {
      const summary = summarizeApiError(e, {
        fallbackTitle: 'Could not start the brief',
      });
      toast.error(summary.title, { description: summary.description });
    }
  };

  return (
    <li className="flex items-start justify-between gap-3 py-3 first:pt-0 last:pb-0">
      <div className="min-w-0 flex-1 space-y-1">
        <p className="truncate text-sm font-medium" title={item.query}>
          {item.query}
        </p>
        <p className="text-xs text-muted-foreground">
          {Math.round(item.impressions).toLocaleString()} impressions ·{' '}
          {item.clicks.toLocaleString()} clicks · CTR {(item.ctr * 100).toFixed(1)}%
        </p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1.5">
        <Badge variant="secondary" className="font-mono tabular-nums">
          #{item.position.toFixed(1)}
        </Badge>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs"
          disabled={create.isPending}
          onClick={() => void onCreate()}
        >
          {create.isPending ? <Spinner className="h-3 w-3" /> : null}
          Create brief
        </Button>
      </div>
    </li>
  );
}

function DecayCard({
  projectId,
  items,
  period,
}: {
  projectId: string;
  items: GscDecayItem[];
  period: {
    current_start: string;
    current_end: string;
    prior_start: string;
    prior_end: string;
  } | null;
}): JSX.Element {
  return (
    <Section>
      <SectionHeader>
        <div>
          <SectionTitle className="flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-muted-foreground" />
            Content decay — refresh these
          </SectionTitle>
          <SectionDescription>
            Pages down &gt;20% impressions week-over-week
            {period ? ` · vs ${period.prior_start}–${period.prior_end}` : ''}
          </SectionDescription>
        </div>
      </SectionHeader>

      {items.length === 0 ? (
        <EmptyState
          variant="inline"
          icon={TrendingDown}
          title="No decaying pages"
          description="Pages that lose more than 20% of impressions week-over-week will appear here as refresh candidates."
        />
      ) : (
        <ul className="divide-y divide-border/60">
          {items.slice(0, 8).map((row) => (
            <DecayRow key={row.page} projectId={projectId} item={row} />
          ))}
        </ul>
      )}
    </Section>
  );
}

function DecayRow({
  projectId,
  item,
}: {
  projectId: string;
  item: GscDecayItem;
}): JSX.Element {
  const refresh = useCreateGscRefreshBrief(projectId);
  const navigate = useNavigate();

  const onRefresh = async (): Promise<void> => {
    try {
      const brief = await refresh.mutateAsync({ page_url: item.page });
      toast.success('Refresh brief started', {
        description: `Improving ${shortPath(item.page)}. Usually takes 2–3 minutes.`,
      });
      navigate({ to: '/briefs/$briefId', params: { briefId: brief.id } });
    } catch (e) {
      const summary = summarizeApiError(e, {
        fallbackTitle: 'Could not start refresh brief',
      });
      toast.error(summary.title, { description: summary.description });
    }
  };

  return (
    <li className="flex items-start justify-between gap-3 py-3 first:pt-0 last:pb-0">
      <div className="min-w-0 flex-1 space-y-1">
        <a
          href={item.page}
          target="_blank"
          rel="noreferrer"
          className="group flex items-center gap-1 truncate text-sm font-medium hover:underline"
          title={item.page}
        >
          <span className="truncate">{shortPath(item.page)}</span>
          <ExternalLink className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-60" />
        </a>
        <p className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-0.5 text-destructive">
            <ArrowDownRight className="h-3 w-3" />
            −{item.drop_pct}% impressions
          </span>
          <span>
            {Math.round(item.impressions_prior).toLocaleString()} →{' '}
            {Math.round(item.impressions_current).toLocaleString()}
          </span>
        </p>
      </div>
      <Button
        size="sm"
        variant="secondary"
        className="h-8 shrink-0 gap-1.5"
        disabled={refresh.isPending}
        onClick={() => void onRefresh()}
      >
        {refresh.isPending ? (
          <Spinner className="h-3.5 w-3.5" />
        ) : (
          <FileEdit className="h-3.5 w-3.5" />
        )}
        Refresh brief
      </Button>
    </li>
  );
}

function shortPath(url: string): string {
  try {
    const u = new URL(url);
    return u.pathname === '/' ? u.hostname : u.pathname;
  } catch {
    return url;
  }
}

/** Hint under the Integrations Google card. */
export function GscInsightsLink(): JSX.Element {
  return (
    <p className="text-xs text-muted-foreground">
      After you connect Search Console, opportunity and content-decay cards appear below.
    </p>
  );
}
